import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatDateTime } from "../lib/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Play, Power, Edit3, ChevronRight, ChevronDown } from "lucide-react";
import VisualWorkflowEditor from "@/super_admin/VisualWorkflowEditor";
import DryRunDialog from "@/super_admin/automation/DryRunDialog";
import RunOutcomeList from "@/super_admin/automation/RunOutcomeList";
import { isDryRun, runStatus } from "@/super_admin/automation/runOutcome";
// Relative: these resolve for real under Jest, where the "@" alias does not exist.
import { automationErrorMessage, refusedActionField, refusedRuleName, ruleNameMessage } from "./automation/automationErrors";
import { actionLabel, configIssueText, triggerLabel } from "./automation/automationCodes";
import { configForSave, invalidConfigFields, ruleNameProblem } from "./automation/actionConfigRules";
import ActionConfigForm from "./automation/ActionConfigForm";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";

// A new rule starts with no action: each one the operator picks asks for its own settings.
const EMPTY_FORM = { name: "", trigger: "tenant.signup", actions: [], enabled: true, conditions: {} };

// A rule's stored actions, whatever a rule saved before validation left there (contract 4.1):
// `null` is none, a lone value is one action, and an action that is not an object is unreadable.
const ruleActions = (rule) => (Array.isArray(rule.actions) ? rule.actions : rule.actions == null ? [] : [rule.actions]);
const actionType = (action) => (action && typeof action === "object" ? action.type : "invalid");
// The stored actions the runner would skip, as the API reports them on every rule (contract
// 4.4): `[{index, type, field}]`, `[]` when every action can run.
const configIssues = (rule) => (Array.isArray(rule.config_issues) ? rule.config_issues : []);

const STATUS_DOT = {
  succeeded: "bg-emerald-400", partial: "bg-amber-400", skipped: "bg-slate-300", failed: "bg-rose-400",
  running: "bg-sky-400", simulated_legacy: "bg-slate-300",
};

// Triggers and action types come from GET /automation/catalogue, which derives them
// from automation_runner's dispatch table. They used to be restated here, so a trigger
// the backend had wired stayed unofferable in this form (BUG-013: "service.interest").
//
// Moved verbatim out of HqAutomation.jsx (lifecycle-policy-contract.md §9: "Custom rules
// (CustomRulesPanel, which is today's HqAutomation body moved verbatim)") when the
// Lifecycle tab was split out. Only the component name changed — same testIds, same
// behaviour, same known defects (docs/automation-module/lifecycle-policy-contract.md §10).
export default function CustomRulesPanel() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  // Reading rules and runs is tenants.read (the route gate); every change to them is settings.write.
  const { rules: canWrite } = useScreenWrites("automation");
  const [loadError, setLoadError] = useState(null);
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [catalogue, setCatalogue] = useState({ triggers: [], actions: [] });
  const [creating, setCreating] = useState(false);
  const [visualEditing, setVisualEditing] = useState(null);
  const [dryRunRule, setDryRunRule] = useState(null);
  const [openRuns, setOpenRuns] = useState(() => new Set());
  const [form, setForm] = useState(EMPTY_FORM);
  // The new-rule form's refusals: the fields to flag per action type, and a form-level sentence.
  const [formFlags, setFormFlags] = useState({});
  const [formError, setFormError] = useState("");
  // Why the name is refused (`ruleNameProblem`'s shape, or the server's `invalid_rule_name`), null when it is not.
  const [nameProblem, setNameProblem] = useState(null);

  const load = async () => {
    try {
      const [r, ru, cat] = await Promise.all([
        api.get("/super-admin/hq/automation/rules"),
        api.get("/super-admin/hq/automation/runs"),
        api.get("/super-admin/hq/automation/catalogue"),
      ]);
      setRules(r.data.items); setRuns(ru.data.items);
      setCatalogue({ triggers: cat.data.triggers || [], actions: cat.data.actions || [] });
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
  };
  useEffect(() => { load(); }, []);
  const TRIGGERS = catalogue.triggers;
  const ACTION_TYPES = catalogue.actions;

  const toggleAction = (type) => {
    const has = form.actions.some((x) => x.type === type);
    setForm({ ...form, actions: has ? form.actions.filter((x) => x.type !== type) : [...form.actions, { type, config: {} }] });
    setFormError("");
  };
  const setActionField = (type, name, value) => {
    setForm((cur) => ({ ...cur, actions: cur.actions.map((a) => (a.type === type ? { ...a, config: { ...a.config, [name]: value } } : a)) }));
    setFormFlags((cur) => ({ ...cur, [type]: (cur[type] || []).filter((f) => f !== name) }));
  };
  // The form is noValidate: every check is made here and said inline, in the operator's
  // language, instead of by the browser's own bubble.
  const create = async (e) => {
    e.preventDefault();
    const badName = ruleNameProblem(form.name);
    setNameProblem(badName);
    if (form.actions.length === 0) { setFormError(t("hauto.cfg.pickAction")); return; }
    const problems = Object.fromEntries(form.actions
      .map((a) => [a.type, invalidConfigFields(a.type, a.config)])
      .filter(([, fields]) => fields.length > 0));
    setFormFlags(problems);
    if (badName || Object.keys(problems).length > 0) { setFormError(t("hauto.cfg.fixFields")); return; }
    setFormError("");
    const body = { ...form, actions: form.actions.map((a) => ({ type: a.type, config: configForSave(a.type, a.config) })) };
    try {
      await api.post("/super-admin/hq/automation/rules", body);
      toast.success(t("hauto.ruleCreated")); setCreating(false); load();
      setForm(EMPTY_FORM); setFormFlags({});
    } catch (err) {
      const refused = refusedActionField(err);
      const action = refused ? form.actions[refused.index] : null;
      if (action) setFormFlags({ [action.type]: [refused.field] });
      if (refusedRuleName(err)) setNameProblem({ code: "invalid" });
      toast.error(automationErrorMessage(err, t));
    }
  };
  const del = async (id) => {
    if (!window.confirm(t("hauto.confirmDelete"))) return;
    try {
      await api.delete(`/super-admin/hq/automation/rules/${id}`);
    } catch (err) { toast.error(automationErrorMessage(err, t)); return; }
    load();
  };
  const toggleRun = (id) => setOpenRuns((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggle = async (r) => {
    try {
      // A partial update: only `enabled` changes (the PUT leaves unsent fields alone).
      await api.put(`/super-admin/hq/automation/rules/${r.rule_id}`, { enabled: !r.enabled });
    } catch (err) { toast.error(automationErrorMessage(err, t)); return; }
    load();
  };
  const installTemplate = async () => {
    try {
      const r = await api.get("/super-admin/automation-templates");
      const choice = window.prompt(`${t("hauto.chooseTemplate")}\n${r.data.map((tpl, i) => `${i+1}. ${tpl.name}`).join("\n")}\n\n${t("hauto.enterNumber", { n: r.data.length })}`);
      const idx = parseInt(choice) - 1;
      if (r.data[idx]) {
        await api.post(`/super-admin/automation-templates/${r.data[idx].key}/install`);
        toast.success(t("hauto.templateInstalled", { name: r.data[idx].name })); load();
      }
    } catch (e) { toast.error(automationErrorMessage(e, t)); }
  };

  if (loadError) {
    return (
      <div data-testid="hq-custom-rules">
        <PageLoadError error={loadError} onRetry={load} testId="hq-custom-rules-load-error" />
      </div>
    );
  }

  return (
    <div data-testid="hq-custom-rules">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hauto.title")}</h1>
          <p className="text-slate-500">{t("hauto.subtitle")}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={installTemplate} variant="outline" className="border-sol-gold/30 text-amber-600 hover:bg-sol-gold/10" data-testid="install-template">
              ⚡ {t("hauto.installTemplate")}
            </Button>
            <Button onClick={() => setVisualEditing({})} variant="outline" className="border-sol-sage/30 text-sol-sage hover:bg-sol-sage/10" data-testid="visual-editor-btn">
              🎨 {t("hauto.visualEditor")}
            </Button>
            <Button onClick={() => setCreating(!creating)} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="new-rule">
              <Plus className="w-4 h-4 mr-2" /> {t("hauto.newRule")}
            </Button>
          </div>
        )}
      </div>

      {canWrite && creating && (
        <form onSubmit={create} noValidate className="p-6 rounded-2xl bg-sol-gold/5 border border-sol-gold/20 mb-6" data-testid="rule-form">
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div>
              <Input placeholder={t("hauto.phRuleName")} aria-label={t("hauto.phRuleName")} value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setNameProblem(null); }}
                aria-required="true" aria-invalid={Boolean(nameProblem)} aria-describedby={nameProblem ? "rule-name-error" : undefined}
                className={`bg-white ${nameProblem ? "border-rose-400" : "border-slate-200"}`} data-testid="rule-name" />
              {nameProblem && (
                <p id="rule-name-error" role="alert" className="text-xs text-rose-600 mt-1" data-testid="rule-name-error">{ruleNameMessage(nameProblem, t)}</p>
              )}
            </div>
            <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="rule-trigger">
              {TRIGGERS.map((tr) => <option key={tr} value={tr} className="bg-white">{triggerLabel(t, tr)}</option>)}
            </select>
          </div>
          <div className="text-xs text-slate-500 mb-2">{t("hauto.actionsHint")}</div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {ACTION_TYPES.map((a) => {
              const on = form.actions.some((x) => x.type === a);
              return (
                <button type="button" key={a} onClick={() => toggleAction(a)} aria-pressed={on}
                  className={`text-xs px-3 py-1 rounded-full border ${on ? "border-sol-gold/40 text-amber-600 bg-sol-gold/10" : "border-slate-200 text-slate-500"}`} data-testid={`action-toggle-${a}`}>
                  {actionLabel(t, a)}
                </button>
              );
            })}
          </div>
          {form.actions.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {form.actions.map((a) => (
                <fieldset key={a.type} className="rounded-xl border border-slate-200 bg-white p-4">
                  <legend className="px-1 text-xs font-medium text-slate-700">{actionLabel(t, a.type)}</legend>
                  <ActionConfigForm type={a.type} config={a.config} invalid={formFlags[a.type] || []}
                    onChange={(name, value) => setActionField(a.type, name, value)} testIdPrefix={`cfg-${a.type}`} />
                </fieldset>
              ))}
            </div>
          )}
          {formError && <p role="alert" className="text-sm text-rose-600 mb-3" data-testid="rule-form-error">{formError}</p>}
          <Button type="submit" className="bg-sol-gold text-black w-full" data-testid="rule-submit">{t("hauto.create")}</Button>
        </form>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="font-sans font-semibold text-lg mb-4">{t("hauto.rulesTitle", { n: rules.length })}</h3>
          <div className="space-y-2">
            {rules.map((r) => (
              <div key={r.rule_id} className="rounded-xl bg-white border border-slate-100 p-4" data-testid={`rule-${r.rule_id}`}>
                <div className="flex items-center gap-3">
                  {canWrite ? (
                    <button onClick={() => toggle(r)} title={t("hauto.enableDisable")} aria-label={t("hauto.enableDisable")} data-testid={`toggle-rule-${r.rule_id}`}>
                      <Power className={`w-4 h-4 ${r.enabled ? "text-emerald-400" : "text-slate-400"}`} />
                    </button>
                  ) : (
                    <Power className={`w-4 h-4 ${r.enabled ? "text-emerald-400" : "text-slate-400"}`} aria-hidden="true" />
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {r.name}
                      {configIssues(r).length > 0 && (
                        <span className="text-[10px] rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5" data-testid={`rule-setup-badge-${r.rule_id}`}>
                          {t("hauto.setup.badge")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">
                      <span className="text-amber-600">{triggerLabel(t, r.trigger)}</span> → {ruleActions(r).map((a) => actionLabel(t, actionType(a))).join(", ")} · {r.runs_count || 0} {t("hauto.runs")}
                    </div>
                  </div>
                  {canWrite && (
                    <>
                      <button onClick={() => setDryRunRule(r)} className="text-emerald-400 hover:text-emerald-600" title={t("hauto.testRun")} aria-label={t("hauto.testRun")} data-testid={`test-${r.rule_id}`}>
                        <Play className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setVisualEditing(r)} className="text-sol-sage hover:text-sol-sage/80" title={t("hauto.editVisually")} aria-label={t("hauto.editVisually")} data-testid={`edit-visual-${r.rule_id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => del(r.rule_id)} className="text-rose-400" data-testid={`del-${r.rule_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
                {configIssues(r).length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900" data-testid={`rule-setup-${r.rule_id}`}>
                    <p className="mb-1">{t("hauto.setup.hint")}</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {configIssues(r).map((issue) => (
                        <li key={`${issue.index}-${issue.field}`}>
                          {t("hauto.setup.item", {
                            n: Number.isInteger(issue.index) ? issue.index + 1 : "",
                            action: actionLabel(t, issue.type),
                            problem: configIssueText(t, issue.field),
                          })}
                        </li>
                      ))}
                    </ul>
                    {canWrite && (
                      <Button type="button" size="sm" variant="outline" onClick={() => setVisualEditing(r)}
                        className="mt-2 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`setup-rule-${r.rule_id}`}>
                        {t("hauto.setup.open")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {rules.length === 0 && <p className="text-slate-400 text-sm py-6 text-center">{t("hauto.noRules")}</p>}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="font-sans font-semibold text-lg mb-4">{t("hauto.recentRuns")}</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {runs.map((run) => {
              const dry = isDryRun(run);
              const status = runStatus(run);
              const open = openRuns.has(run.run_id);
              const results = run.action_results || [];
              return (
                <div key={run.run_id} className="text-sm p-2 rounded-lg bg-white" data-testid={`run-${run.run_id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`}></span>
                    <div className="flex-1">
                      <div className="text-xs text-slate-800/70">{run.rule_name}</div>
                      <div className="text-[10px] text-slate-400">{triggerLabel(t, run.trigger)} · {run.actions_executed} {t("hauto.actions")} · {formatDateTime(run.started_at, lang)}</div>
                    </div>
                    <span
                      className={`text-[10px] rounded-full border px-2 py-0.5 ${dry ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600"}`}
                      data-testid={`run-badge-${run.run_id}`}
                    >
                      {dry ? t("hauto.dryRun.badge") : t(`hauto.status.${status}`)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleRun(run.run_id)}
                      aria-expanded={open}
                      aria-label={t(open ? "hauto.hideActions" : "hauto.showActions")}
                      className="text-slate-400 hover:text-slate-600"
                      data-testid={`run-toggle-${run.run_id}`}
                    >
                      {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  {open && (
                    <div className="mt-2 pl-5" data-testid={`run-actions-${run.run_id}`}>
                      <RunOutcomeList results={results} dry={dry} testId={`run-outcomes-${run.run_id}`} />
                    </div>
                  )}
                </div>
              );
            })}
            {runs.length === 0 && <p className="text-slate-400 text-sm py-6 text-center">{t("hauto.noRuns")}</p>}
          </div>
        </div>
      </div>

      {dryRunRule && <DryRunDialog rule={dryRunRule} onClose={() => setDryRunRule(null)} onDone={load} />}

      {visualEditing && (
        <VisualWorkflowEditor
          triggers={TRIGGERS}
          actions={ACTION_TYPES}
          rule={visualEditing.rule_id ? visualEditing : null}
          onClose={() => setVisualEditing(null)}
          onSaved={() => { setVisualEditing(null); load(); }}
        />
      )}
    </div>
  );
}
